// updates-logic.js
// Pure functions for the Update Feed — no side effects, no framework deps.
//
// A Binder Update is the full set of Card Changes a binder underwent in one
// sync. A Card Change is one of: 'added', 'removed', 'quantity' (carrying
// old→new). This module groups Card Changes by type, formats deltas, buckets
// Binder Updates under day headers, and does the pagination math. See
// CONTEXT.md ("Binder updates & feed").

export const CHANGE_ADDED = 'added';
export const CHANGE_REMOVED = 'removed';
export const CHANGE_QUANTITY = 'quantity';

const FOIL_FINISHES = new Set(['foil', 'etched']);

/**
 * True if a finish string denotes a foil/etched printing.
 * @param {string|null|undefined} finish
 * @returns {boolean}
 */
export function isFoilFinish(finish) {
  return FOIL_FINISHES.has((finish || '').toLowerCase());
}

/**
 * The numeric display price for a Card Change. The server resolves the
 * finish-appropriate USD value (`price_usd`) the same way collection-api does,
 * so the client never reimplements the finish→field ladder. Missing/removed
 * cards carry null, which reads as 0 for ordering.
 * @param {Object} change - a Card Change carrying `price_usd`
 * @returns {number}
 */
export function changePrice(change) {
  const v = change?.price_usd;
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

/**
 * Format a Card Change's quantity delta into a display string.
 *   added    → '+N'
 *   removed  → '−N'   (uses a real minus sign, U+2212)
 *   quantity → 'old→new'
 * @param {Object} change - a Card Change
 * @returns {string}
 */
export function formatDelta(change) {
  switch (change.change_type) {
    case CHANGE_ADDED:
      return `+${change.new_qty ?? 0}`;
    case CHANGE_REMOVED:
      return `−${change.old_qty ?? 0}`;
    case CHANGE_QUANTITY:
      return `${change.old_qty ?? 0}→${change.new_qty ?? 0}`;
    default:
      return '';
  }
}

/**
 * Partition a Binder Update's Card Changes by type.
 * Additions come back sorted price-descending (the biggest news first, matching
 * the Discord Binder Update Notification's ordering). Removals and quantity
 * changes keep their incoming order.
 * @param {Array<Object>} changes
 * @returns {{ added: Array, removed: Array, quantity: Array }}
 */
export function groupChangesByType(changes) {
  const added = [];
  const removed = [];
  const quantity = [];

  for (const c of changes || []) {
    if (c.change_type === CHANGE_ADDED) added.push(c);
    else if (c.change_type === CHANGE_REMOVED) removed.push(c);
    else if (c.change_type === CHANGE_QUANTITY) quantity.push(c);
  }

  added.sort((a, b) => changePrice(b) - changePrice(a));

  return { added, removed, quantity };
}

/**
 * Select the top-N Card Changes to preview in an update block, plus how many
 * are hidden. Additions are surfaced first (price-descending), then quantity
 * changes, then removals — the biggest news leads. The "+N more" remainder
 * links to the detail view.
 * @param {Array<Object>} changes - all Card Changes for one Binder Update
 * @param {number} limit - top-N to show (default 10)
 * @returns {{ preview: Array, remaining: number }}
 */
export function topChanges(changes, limit = 10) {
  const { added, removed, quantity } = groupChangesByType(changes);
  const ordered = [...added, ...quantity, ...removed];
  const preview = ordered.slice(0, limit);
  return { preview, remaining: Math.max(0, ordered.length - preview.length) };
}

/**
 * The local YYYY-MM-DD key for a timestamp, used to bucket updates under a day.
 * @param {string|Date} ts - ISO timestamp or Date
 * @returns {string}
 */
export function dayKey(ts) {
  const d = ts instanceof Date ? ts : new Date(ts);
  if (Number.isNaN(d.getTime())) return 'unknown';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Bucket a reverse-chronological list of Binder Updates under day headers.
 * Preserves the incoming order (the feed already arrives newest-first), so the
 * buckets come out newest-day-first and updates within a day stay ordered.
 * @param {Array<Object>} updates - each with a synced_at timestamp
 * @returns {Array<{ day: string, updates: Array }>}
 */
export function bucketByDay(updates) {
  const buckets = [];
  const indexByDay = new Map();

  for (const u of updates || []) {
    const key = dayKey(u.synced_at);
    if (!indexByDay.has(key)) {
      indexByDay.set(key, buckets.length);
      buckets.push({ day: key, updates: [] });
    }
    buckets[indexByDay.get(key)].updates.push(u);
  }

  return buckets;
}

/**
 * Pagination math for the numbered-page control. Pages are 1-indexed; the feed
 * defaults to the newest page (page 1, since the feed is reverse-chron).
 * @param {number} total - total update count
 * @param {number} pageSize
 * @param {number} page - current 1-indexed page
 * @returns {{ totalPages: number, page: number, hasPrev: boolean, hasNext: boolean,
 *            startIndex: number, endIndex: number }}
 */
export function paginationInfo(total, pageSize, page) {
  const size = Math.max(1, pageSize || 1);
  const totalPages = Math.max(1, Math.ceil((total || 0) / size));
  const clamped = Math.min(Math.max(1, page || 1), totalPages);
  const startIndex = (clamped - 1) * size;
  const endIndex = Math.min(startIndex + size, total || 0);
  return {
    totalPages,
    page: clamped,
    hasPrev: clamped > 1,
    hasNext: clamped < totalPages,
    startIndex,
    endIndex,
  };
}
